/**
 * Zona 2: matriz lotes × setores. Clique no cabeçalho da coluna liga/desliga
 * o setor em todos os lotes; clique no número do lote liga/desliga a linha;
 * clicar e arrastar pinta várias células, como numa planilha. Célula de setor
 * que não se aplica ao lote (sem item de Terceiros, por exemplo) fica
 * desabilitada com o motivo no tooltip (lote acima de 10 ambientes também);
 * nada deixa de sair em silêncio.
 */
import React, { useCallback, useEffect, useRef } from "react";

import { IDS_DA_MATRIZ, setorPorId } from "../../utils/apontamentos";
import { BotaoSecundario, Bloco, CelulaMatriz, Matriz, Vazio } from "./styles";

const ABREVIACOES = { compras: "CMP", maquinas: "MAQ", marcenaria: "MAR", terceiros: "TER", acabamento: "ACA", expedicao: "EXP", carregamento: "CAR" };

const PRESETS = [
  { id: "todos", rotulo: "Todos", atalho: "T" },
  { id: "producao", rotulo: "Produção" },
  { id: "terceiros", rotulo: "Só Terceiros" },
  { id: "limpar", rotulo: "Limpar" },
];

export default function MatrizImpressao({ lotes, selecao, aplicavel, motivoDesabilitado, onDefinir, onPreset }) {
  const pintura = useRef(null);

  useEffect(() => {
    const terminar = () => {
      pintura.current = null;
    };
    window.addEventListener("pointerup", terminar);
    window.addEventListener("pointercancel", terminar);
    return () => {
      window.removeEventListener("pointerup", terminar);
      window.removeEventListener("pointercancel", terminar);
    };
  }, []);

  const ligada = useCallback((lote, setorId) => Boolean(selecao.get(lote.chave)?.has(setorId)), [selecao]);

  const iniciarPintura = (lote, setorId) => (evento) => {
    if (evento.button !== 0) return;
    const valor = !ligada(lote, setorId);
    pintura.current = valor;
    onDefinir([{ lote, setorId, valor }]);
  };

  const continuarPintura = (lote, setorId) => () => {
    if (pintura.current === null || !aplicavel(lote, setorId)) return;
    if (ligada(lote, setorId) !== pintura.current) onDefinir([{ lote, setorId, valor: pintura.current }]);
  };

  const alternarColuna = (setorId) => {
    const alvo = lotes.filter((lote) => aplicavel(lote, setorId));
    const valor = !alvo.every((lote) => ligada(lote, setorId));
    onDefinir(alvo.map((lote) => ({ lote, setorId, valor })));
  };

  const alternarLinha = (lote) => {
    const alvo = IDS_DA_MATRIZ.filter((setorId) => aplicavel(lote, setorId));
    const valor = !alvo.every((setorId) => ligada(lote, setorId));
    onDefinir(alvo.map((setorId) => ({ lote, setorId, valor })));
  };

  // Teclado: Enter/Espaço alterna a célula (o pointerdown cobre o mouse e o toque).
  const teclaNaCelula = (lote, setorId) => (evento) => {
    if (evento.key !== "Enter" && evento.key !== " ") return;
    evento.preventDefault();
    onDefinir([{ lote, setorId, valor: !ligada(lote, setorId) }]);
  };

  return (
    <Bloco aria-labelledby="titulo-matriz">
      <header>
        <span className="passo" aria-hidden="true">2</span>
        <h2 id="titulo-matriz">Setores de cada lote</h2>
      </header>
      {lotes.length === 0 ? (
        <Vazio>Marque um ou mais lotes acima.</Vazio>
      ) : (
        <Matriz>
          <table>
            <thead>
              <tr>
                <th className="lote" scope="col" aria-label="Lote" />
                {IDS_DA_MATRIZ.map((setorId) => (
                  <th key={setorId} scope="col">
                    <button type="button" title={`${setorPorId(setorId).titulo}: ligar ou desligar em todos os lotes`} onClick={() => alternarColuna(setorId)}>
                      {ABREVIACOES[setorId]}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lotes.map((lote) => (
                <tr key={lote.chave}>
                  <th scope="row" className="lote">
                    <button type="button" title={`Lote ${lote.rotulo}: ligar ou desligar todos os setores`} onClick={() => alternarLinha(lote)}>
                      {lote.numero}
                    </button>
                  </th>
                  {IDS_DA_MATRIZ.map((setorId) => {
                    const pode = aplicavel(lote, setorId);
                    const setor = setorPorId(setorId);
                    const marcada = ligada(lote, setorId);
                    return (
                      <td key={setorId}>
                        <CelulaMatriz
                          type="button"
                          $ligada={marcada}
                          disabled={!pode}
                          aria-pressed={marcada}
                          aria-label={`${setor.titulo}, lote ${lote.rotulo}`}
                          title={pode ? setor.titulo : motivoDesabilitado(lote, setorId)}
                          onPointerDown={iniciarPintura(lote, setorId)}
                          onPointerEnter={continuarPintura(lote, setorId)}
                          onKeyDown={teclaNaCelula(lote, setorId)}
                        >
                          {marcada ? "✓" : ""}
                        </CelulaMatriz>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="presets">
            {PRESETS.map((preset) => (
              <BotaoSecundario key={preset.id} type="button" onClick={() => onPreset(preset.id)} title={preset.atalho ? `Atalho: ${preset.atalho}` : undefined}>
                {preset.rotulo}
              </BotaoSecundario>
            ))}
          </div>
        </Matriz>
      )}
    </Bloco>
  );
}
